import React, { useMemo } from "react";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import type { RootState } from "@redux/store/store";
import {
  GROUP_HEALTH_ACTIVE,
  GROUP_HEALTH_EMPTY,
  GROUP_HEALTH_NO_RECIPIENTS,
  GROUP_HEALTH_NO_RESOURCES,
  setNotificationsGroupsStatusSelection,
} from "@redux/slices/notificationsGroupsFiltersSlice";
import MultiFilterDropdown, {
  type MultiFilterFieldConfig,
  type MultiFilterValue,
} from "@ui-components/Filter/MultiFilterDropdown";
import {
  computeGroupHealthStatus,
  GROUP_HEALTH_LABEL,
} from "./GroupsData";

const STATUS_FIELD_ID = "status";

const STATUS_ORDER = [
  GROUP_HEALTH_ACTIVE,
  GROUP_HEALTH_EMPTY,
  GROUP_HEALTH_NO_RECIPIENTS,
  GROUP_HEALTH_NO_RESOURCES,
] as const;

/**
 * Global filter for Notification Groups — mounted in the SideNav slot when
 * Account → Notifications is on the Groups tab (see `getRenderTabsByRoute`).
 */
const NotificationsGroupsFilters: React.FC = () => {
  const dispatch = useAppDispatch();
  const groups = useAppSelector(
    (s: RootState) => s.notificationsV2Groups.groups
  );
  const { statusFilterSelection } = useAppSelector(
    (s: RootState) => s.notificationsGroupsFilters
  );

  const counts = useMemo(() => {
    const map = new Map<string, number>();
    for (const id of STATUS_ORDER) map.set(id, 0);
    for (const g of groups) {
      const st = computeGroupHealthStatus(g);
      map.set(st, (map.get(st) ?? 0) + 1);
    }
    return map;
  }, [groups]);

  const fields: MultiFilterFieldConfig[] = useMemo(() => {
    const options = STATUS_ORDER.filter((id) => (counts.get(id) ?? 0) > 0).map(
      (id) => ({
        id,
        label: GROUP_HEALTH_LABEL[id],
        count: counts.get(id) ?? 0,
      })
    );
    if (options.length === 0) return [];
    return [
      {
        id: STATUS_FIELD_ID,
        label: "Status",
        options,
        searchable: false,
        emptyOptionsMessage: "No groups",
      },
    ];
  }, [counts]);

  const value: MultiFilterValue = useMemo(
    () => ({
      [STATUS_FIELD_ID]: statusFilterSelection,
    }),
    [statusFilterSelection]
  );

  const handleApply = (next: MultiFilterValue) => {
    const sel = (next[STATUS_FIELD_ID] ?? []) as typeof statusFilterSelection;
    dispatch(setNotificationsGroupsStatusSelection(sel));
  };

  if (fields.length === 0) return null;

  return (
    <div className="w-full">
      <MultiFilterDropdown
        fields={fields}
        value={value}
        onApply={handleApply}
        triggerLabel="Filters"
        cardTitle="Filters"
      />
    </div>
  );
};

export default NotificationsGroupsFilters;
